import moment from 'moment';
import helpers from '../../economy/helpers/index.js';
import { VoucherCategory } from '../../economy/vouchers/index.js';
import { z } from 'zod/v4';
export var PaymentPlanStatus;
(function (PaymentPlanStatus) {
    PaymentPlanStatus["CREATED"] = "CREATED";
    PaymentPlanStatus["ACTIVE"] = "ACTIVE";
    PaymentPlanStatus["FINISHED"] = "FINISHED";
    PaymentPlanStatus["CANCELED"] = "CANCELED";
    PaymentPlanStatus["DELETED"] = "DELETED";
})(PaymentPlanStatus || (PaymentPlanStatus = {}));
export var PaymentPlanLogType;
(function (PaymentPlanLogType) {
    PaymentPlanLogType["ErrorSettled"] = "ERROR_SETTLED";
    PaymentPlanLogType["Settled"] = "SETTLED";
    PaymentPlanLogType["DeltaShift"] = "DELTA_SHIFT";
    PaymentPlanLogType["CancelPlan"] = "CANCEL_PLAN";
    PaymentPlanLogType["Activated"] = "ACTIVATED";
    PaymentPlanLogType["Recalculated"] = "RECALCULATED";
})(PaymentPlanLogType || (PaymentPlanLogType = {}));
export var PaymentPlanEntryStatus;
(function (PaymentPlanEntryStatus) {
    PaymentPlanEntryStatus["PENDING"] = "PENDING";
    PaymentPlanEntryStatus["REMINDED"] = "REMINDED";
    PaymentPlanEntryStatus["SETTLED"] = "SETTLED";
    PaymentPlanEntryStatus["AWAITING"] = "AWAITING";
    PaymentPlanEntryStatus["ERRORSETTLED"] = "ERRORSETTLED";
    PaymentPlanEntryStatus["CANCELED"] = "CANCELED";
    PaymentPlanEntryStatus["OVERDUE"] = "OVERDUE";
})(PaymentPlanEntryStatus || (PaymentPlanEntryStatus = {}));
export var PaymentPlanEntryTypes;
(function (PaymentPlanEntryTypes) {
    PaymentPlanEntryTypes["UPFRONT"] = "UPFRONT";
    PaymentPlanEntryTypes["NORMAL"] = "NORMAL";
    PaymentPlanEntryTypes["REST"] = "REST";
})(PaymentPlanEntryTypes || (PaymentPlanEntryTypes = {}));
export var Interval;
(function (Interval) {
    Interval["WEEK"] = "WEEK";
    Interval["TWO-WEEKS"] = "TWO-WEEKS";
    Interval["MONTH"] = "MONTH";
    Interval["TWO-MONTHS"] = "TWO-MONTHS";
    Interval["QUARTERLY"] = "QUARTERLY";
})(Interval || (Interval = {}));
export var PaymentPlanConsoleLogType;
(function (PaymentPlanConsoleLogType) {
    PaymentPlanConsoleLogType["REMINDER"] = "reminder";
    PaymentPlanConsoleLogType["DEADLINE"] = "deadline";
    PaymentPlanConsoleLogType["CANCEL"] = "cancel";
    PaymentPlanConsoleLogType["HANDLER"] = "handler";
    PaymentPlanConsoleLogType["SETTLECARDS"] = "settle-cards";
    PaymentPlanConsoleLogType["SETTLEBSANDLS"] = "settle-bs-and-ls";
    PaymentPlanConsoleLogType["SettleRanTwice"] = "settle-ran-twice";
})(PaymentPlanConsoleLogType || (PaymentPlanConsoleLogType = {}));
export const PaymentPlansConfigSchema = z.object({
    plans: z.array(z.object({
        interval: z.enum(Interval),
        duration: z.number(),
        id: z.uuid(),
    })),
    disablePaymentPlans: z.boolean().optional(),
    requireCentralRegisterId: z.boolean().optional(),
    minEntryAmount: z.number(),
    activationDeadlineDays: z.number().optional().nullable(),
    deadlineDays: z.number().optional(),
    cancelDays: z.number().optional(),
    reminderDays: z.number().optional(),
});
export const defaultPaymentPlansConfig = {
    plans: [
        {
            id: '300be1dd-63cb-43f0-a110-eabaa0416297',
            interval: Interval.MONTH,
            duration: 3,
        },
        {
            id: 'e38c6d93-09d9-40d5-9611-1324c9ea9c83',
            interval: Interval.MONTH,
            duration: 6,
        },
        {
            id: '3cd046f4-650d-43bd-9091-43d12e9eb8ea',
            interval: Interval.MONTH,
            duration: 9,
        },
        {
            id: 'c575de9e-25a3-4b64-ad7b-402ffb40d9d1',
            interval: Interval.MONTH,
            duration: 12,
        },
    ],
    minEntryAmount: 5000,
    disablePaymentPlans: false,
    requireCentralRegisterId: false,
    deadlineDays: 5,
    cancelDays: 5,
    reminderDays: 5,
};
export const getCurrentPaymentPlan = (paymentPlans) => {
    if (!paymentPlans)
        return null;
    const currentActivePlan = paymentPlans.find((plan) => [PaymentPlanStatus.ACTIVE].includes(plan.status));
    if (currentActivePlan) {
        return currentActivePlan;
    }
    const currentCreatedPlan = paymentPlans.find((plan) => [PaymentPlanStatus.CREATED].includes(plan.status));
    if (currentCreatedPlan) {
        return currentCreatedPlan;
    }
    const currentPlan = paymentPlans.find((plan) => [PaymentPlanStatus.FINISHED].includes(plan.status));
    return currentPlan || null;
};
export const getAmount = {
    upfront: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.UPFRONT);
        return entry && entry.amount;
    },
    entry: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.NORMAL);
        return entry && entry.amount;
    },
    total: (plan) => plan.reduce((acc, entry) => acc + (entry.amount || 0), 0),
    rest: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.REST);
        return entry && entry.amount;
    },
};
export const PaymentPlanEntrySchema = z.object({
    amount: z.number(),
    cancel: z.string(),
    date: z.string(),
    reminder: z.string(),
    deadline: z.string(),
    type: z.enum(PaymentPlanEntryTypes),
});
export const CreatePaymentPlanSchema = z.object({
    activationTrigger: z.boolean().optional(),
    selectedPaymentPlanId: z.uuid().optional(),
    plan: z
        .array(PaymentPlanEntrySchema)
        .nullish()
        .transform((x) => x ?? undefined),
    config: z
        .object({
        startDate: z.string(),
        interval: z.enum(Interval),
        duration: z.number().nullish(),
        amount: z.number().nullish(),
        amountUpfront: z.number().nullish(),
        dateUpfront: z.string().nullish(),
        interestFree: z
            .object({
            from: z.string(),
            to: z.string().nullable(),
        })
            .nullish(),
    })
        .nullish()
        .transform((x) => x ?? null),
});
export const PatchPaymentPlanSchema = z.object({
    status: z.enum(PaymentPlanStatus).optional(),
});
export const calculateInterestPerEntry = (vouchers, documents, expirationSettings, entries, index) => {
    const entry = entries[index];
    const previousEntry = entries[index - 1];
    if (!entry)
        throw 'Could not find entry with that index';
    if (!previousEntry) {
        const _vouchersToday = helpers.interest.calculate(helpers.vouchers.postProcessing(vouchers, {
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
            includeIrrecoverableVouchers: false,
        }), documents, expirationSettings, moment().format());
        const interestBalanceToday = helpers.overview.balanceCategory(_vouchersToday, VoucherCategory.INTEREST);
        return interestBalanceToday - (entry.interestAmount || 0);
    }
    else {
        return (previousEntry.interestAmount || 0) - (entry.interestAmount || 0);
    }
};
//# sourceMappingURL=index.js.map